import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Container,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  GetApp as GetAppIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Apple as AppleIcon,
  Share as ShareIcon,
  AddBox as AddBoxIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { schoolName, schoolAddress, themeColor, loading: configLoading } = useAppConfig();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // If authenticated, show logging state and redirect after a delay
    if (!authLoading && !configLoading && isAuthenticated) {
      setIsRedirecting(true);
      // Small delay to show the "Logging..." state
      setTimeout(() => {
        navigate('/my-child', { replace: true });
      }, 1500);
    }
  }, [isAuthenticated, authLoading, configLoading, navigate]);

  // PWA Install detection
  useEffect(() => {
    // Check if already installed
    const checkInstalled = () => {
      if (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true ||
        document.referrer.includes('android-app://')
      ) {
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    if (checkInstalled()) {
      return;
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    if (isIOSDevice) {
      setIsIOS(true);
      setCanInstall(true);
    }

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setCanInstall(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSDialog(true);
      return;
    }

    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setCanInstall(false);
  };

  if (authLoading || configLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Don't show separate loading screen - show landing page with disabled button

  const primaryColor = themeColor || theme.palette.primary.main;

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgb = hexToRgb(primaryColor);
  let gradientBackground = `linear-gradient(135deg, ${primaryColor} 0%, ${theme.palette.primary.dark || primaryColor} 100%)`;
  if (rgb) {
    const darkerR = Math.max(0, Math.floor(rgb.r * 0.7));
    const darkerG = Math.max(0, Math.floor(rgb.g * 0.7));
    const darkerB = Math.max(0, Math.floor(rgb.b * 0.7));
    const darkerShade = `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
    gradientBackground = `linear-gradient(135deg, ${primaryColor} 0%, ${darkerShade} 100%)`;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: 'white',
      }}
    >
      {/* Top 20% Image Area */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '25%',
          overflow: 'hidden',
          zIndex: 0,
        }}
      >
        <Box
          component="img"
          src="https://webapi.entab.info/api/image/STHSM/public/Images/sld1.jpg"
          alt="School Banner"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.9,
          }}
        />
        {/* Overlay for better text/image contrast if needed */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 100%)`,
          }}
        />
      </Box>

      {/* White Box - starts from 20% and extends to bottom */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'background.paper',
          borderTopLeftRadius: { xs: '24px', sm: '32px' },
          borderTopRightRadius: { xs: '24px', sm: '32px' },
          boxShadow: '0 -4px 30px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Content Container */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 3,
            py: 4,
          }}
        >
          <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
            {/* School Logo */}
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -8,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${primaryColor}22, ${primaryColor}11)`,
                    zIndex: -1,
                  },
                }}
              >
                <img
                  src="/school_logo.png"
                  alt="School Logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                  style={{
                    width: isMobile ? 120 : 150,
                    height: isMobile ? 120 : 150,
                  }}
                />
              </Box>
            </Box>

            {/* School Name */}
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 1,
                color: 'text.primary',
                fontSize: { xs: '1.75rem', sm: '1.9rem' },
                letterSpacing: '-0.5px',
              }}
            >
              {schoolName || 'School Management'}
            </Typography>

            {/* School Address */}
            {schoolAddress && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ 
                  mb: 2, 
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  fontWeight: 400,
                  fontStyle: 'italic',
                }}
              >
                {schoolAddress}
              </Typography>
            )}

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ 
                mb: 4, 
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                fontWeight: 500,
              }}
            >
              Parent Portal
            </Typography>

            {/* Login Button or Logging State */}
            {isAuthenticated && isRedirecting ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 3,
                  mb: 2,
                  gap: 1.5,
                }}
              >
                <CircularProgress size={16} sx={{ color: primaryColor }} />
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    fontSize: '1.1rem',
                  }}
                >
                  Logging In...
                </Typography>
              </Box>
            ) : (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleLoginClick}
                sx={{
                  py: 1.75,
                  borderRadius: 3,
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                    transform: 'translateY(-3px)',
                    background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                  },
                  transition: 'all 0.3s ease',
                  mb: 2,
                }}
              >
                Login
              </Button>
            )}

            {/* Install App Button */}
            {canInstall && !isInstalled && (
              <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<GetAppIcon />}
                onClick={handleInstallClick}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderWidth: 2,
                  borderColor: primaryColor,
                  color: primaryColor,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: primaryColor,
                    bgcolor: `${primaryColor}10`,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {'Install App'}
              </Button>
            )}
          </Container>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            py: 2,
            px: 3,
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Developed by{' '}
            <strong style={{ fontWeight: 700 }}>
              <a
                href="https://ptshub.in/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.target.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.target.style.textDecoration = 'none';
                }}
              >
                PTS Hub
              </a>
            </strong>
          </Typography>
        </Box>
      </Box>

      {/* iOS Install Instructions Dialog */}
      <Dialog
        open={showIOSDialog}
        onClose={() => setShowIOSDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AppleIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Add to Home Screen
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            To install this app on your iPhone or iPad:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <ShareIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Tap the Share button"
                secondary="Look for the share icon at the bottom of your Safari browser"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AddBoxIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Select 'Add to Home Screen'"
                secondary="Scroll down in the share menu to find this option"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PhoneAndroidIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Confirm and tap 'Add'"
                secondary="The app will appear on your home screen like a native app"
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIOSDialog(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandingPage;

